// Deeplink Landing Page — backend API client
import axios from "axios";
import { getDeeplinkApiBase } from "./utils";
import { logLandingEvent } from "./analytics";

const client = axios.create({
  timeout: 8000,
  headers: { "Content-Type": "application/json" },
});

/**
 * GET /deeplink/v1/resolve — Section 4.1
 * Resolves the universal link and returns routing instructions.
 *
 * @param {Object} params
 * @param {string} params.destination  Required — target destination
 * @param {string} params.platform     Required — ios | android | desktop
 * @param {boolean} [params.app_installed]
 * @param {string} [params.user_state] logged_in | not_logged_in | no_account
 * @param {string} [params.lang]       Currently 'th' only
 * @param {string} [params.ref_id]     Tracking reference
 */
export const resolveDeeplink = async ({
  destination,
  platform,
  app_installed,
  user_state,
  lang,
  ref_id,
}) => {
  const params = { destination, platform };
  if (app_installed !== undefined) params.app_installed = app_installed;
  if (user_state) params.user_state = user_state;
  if (lang) params.lang = lang;
  if (ref_id) params.ref_id = ref_id;

  const res = await client.get(`${getDeeplinkApiBase()}/resolve`, { params });
  return res.data;
};

/**
 * POST /deeplink/v1/tracking/event — Section 4.4
 * Server-side tracking fallback. The mobile app fires events via the Firebase
 * SDK directly; this is used by the web landing page where the JS SDK may not
 * be loaded.
 */
export const trackEvent = async ({
  event_name,
  destination,
  platform,
  user_state,
  ref_id,
}) => {
  // B2CAPPTH-2964: report straight to Firebase from the browser as well as
  // posting to be-deeplink. The spec puts these two events on the server, but
  // that service is not deployed — /tracking/event answers 403 — so until it is,
  // the POST below reaches nothing and this is the only path that produces data.
  //
  // Reports into the same Firebase project the mobile app uses, so the click
  // and the events the app fires afterwards belong to one funnel.
  //
  // Both calls live here on purpose: when be-deeplink ships, the same click
  // starts being counted twice, and this is the single place to switch one side
  // off. See analytics.js for which side to keep and why.
  logLandingEvent(event_name, { destination, platform, user_state, ref_id });

  try {
    await client.post(`${getDeeplinkApiBase()}/tracking/event`, {
      event_name,
      destination,
      platform,
      user_state,
      ref_id,
      timestamp: new Date().toISOString(),
    });
  } catch (err) {
    // Tracking failures must never block the user journey.
    // eslint-disable-next-line no-console
    console.warn("[deeplink] tracking event failed:", event_name, err?.message);
  }
};
